import { ReactElement, useEffect } from "react";
import { t } from "@lingui/core/macro";
import { useForm } from "react-hook-form";

import { Button } from "_/components/button";
import { Form } from "_/components/form";
import { Logo } from "_/components/logo";
import { useLocation } from "_/components/router";
import { FormTextField } from "_/components/text-field";
import { useAddToast } from "_/components/toasts";
import { H5, P } from "_/components/typography";
import { useBootstrap, useBootstrapStatus } from "_/data/bootstrap";
import { routeUrls } from "_/routes";

import * as S from "./styled";

type BootstrapForm = {
  org: string;
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
};

const defaultValues: BootstrapForm = {
  org: "",
  name: "",
  email: "",
  password: "",
  confirmPassword: "",
};

/**
 * First-run setup page: creates the installation's initial organization and
 * superadmin user. The backing endpoint only works while no users exist, so
 * the page redirects to the login page on already-initialized installations.
 */
export const BootstrapView = (): ReactElement => {
  const [_location, setLocation] = useLocation();
  const addToast = useAddToast();
  const { data: status } = useBootstrapStatus();
  const bootstrap = useBootstrap();
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm({ defaultValues, mode: "onChange" });

  useEffect(() => {
    if (status && !status.required) {
      setLocation(routeUrls.public.login, { replace: true });
    }
  }, [status, setLocation]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      await bootstrap.mutateAsync({
        email: data.email,
        name: data.name,
        password: data.password,
        org: { name: data.org },
      });
    } catch {
      // The mutation's onError handler raises the error toast.
      return;
    }

    setLocation(routeUrls.public.login);

    addToast({
      kind: "success",
      title: t`common.success`,
      content: t`components.bootstrap.success-toast ${data.email}`,
      timeout: 10_000,
    });
  });

  return (
    <S.Wrapper>
      <S.Card>
        <S.Brand>
          <Logo style={{ width: 140 }} />
        </S.Brand>
        <S.FormWrapper>
          <H5>{t`components.bootstrap.title`}</H5>
          <P>{t`components.bootstrap.description`}</P>
          <Form onSubmit={onSubmit}>
            <FormTextField
              label={t`components.bootstrap.org-label`}
              control={control}
              name="org"
              rules={{ required: t`components.bootstrap.org-required` }}
            />
            <FormTextField
              label={t`components.bootstrap.name-label`}
              control={control}
              name="name"
              rules={{ required: t`components.bootstrap.name-required` }}
            />
            <FormTextField
              label={t`common.email`}
              control={control}
              name="email"
              type="email"
              autoComplete="username"
              rules={{ required: t`components.bootstrap.email-required` }}
            />
            <FormTextField
              label={t`common.password`}
              control={control}
              name="password"
              type="new-password"
              rules={{ required: t`common.password-required` }}
            />
            <FormTextField
              label={t`components.bootstrap.confirm-password-label`}
              control={control}
              name="confirmPassword"
              type="password"
              autoComplete="new-password"
              rules={{
                required: t`common.password-required`,
                validate: (value, formValues) => {
                  return (
                    value === formValues.password || t`common.password-mismatch`
                  );
                },
              }}
            />
            <Button
              type="submit"
              size="medium"
              kind="primary"
              disabled={!isValid || bootstrap.isPending}
            >
              {t`components.bootstrap.submit`}
            </Button>
          </Form>
        </S.FormWrapper>
      </S.Card>
    </S.Wrapper>
  );
};
